import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, NotebookText } from 'lucide-react';
import { Button } from 'components/ui/button';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { useAuthContext } from 'modules/auth';
import { PERMISSIONS } from 'modules/auth/permissions';
import * as Notebooks from 'modules/notebooks';
import { useCollapseSidebarOnMount, useIsMobile } from 'hooks';
import { MobileEmpty, MobileError, MobileScreen, MobileSkeleton, ScreenHeader } from 'components/mobile';
import { NotebookWorkspace } from './NotebookWorkspace';

/** The Notebooks grid; also where a missing notebook sends the user. */
const NOTEBOOKS_PATH = '/app/documents';

interface NotebookStatusProps {
  isLoading: boolean;
  isNotFound: boolean;
  onRetry: () => void;
  phone: boolean;
}

/** What shows instead of the workspace while the notebook loads, is missing, or failed to load. */
const NotebookStatus: React.FC<NotebookStatusProps> = ({ isLoading, isNotFound, onRetry, phone }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const backLink = (
    <Button asChild variant="outline">
      <Link to={NOTEBOOKS_PATH}>{t('notebooks.back_to_notebooks')}</Link>
    </Button>
  );

  if (phone) {
    return (
      <>
        <ScreenHeader title={t('notebooks.title')} onBack={() => navigate(NOTEBOOKS_PATH)} />
        {isLoading ? (
          <div aria-busy="true">
            <MobileSkeleton rows={3} />
          </div>
        ) : isNotFound ? (
          <MobileEmpty
            icon={<NotebookText size={22} />}
            title={t('notebooks.not_found_title')}
            description={t('notebooks.not_found_desc')}
            action={backLink}
          />
        ) : (
          <MobileError onRetry={onRetry} />
        )}
      </>
    );
  }

  if (isLoading) {
    return (
      <div aria-busy="true" className="flex flex-col gap-3">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-[calc(100dvh-14rem)] w-full rounded-lg" />
      </div>
    );
  }
  if (isNotFound) {
    return (
      <div className="flex flex-col items-center gap-1 pb-8 pt-4">
        <EmptyState
          icon={<NotebookText size={28} />}
          title={t('notebooks.not_found_title')}
          description={t('notebooks.not_found_desc')}
        />
        {backLink}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1 pb-8 pt-4">
      <EmptyState
        icon={<AlertCircle size={28} />}
        title={t('failed_to_load')}
        secondaryAction={{ label: t('retry'), onClick: onRetry }}
      />
      {backLink}
    </div>
  );
};

/**
 * One notebook's workspace (Sources · Chat · Studio) at `/app/documents/:notebookId`.
 * Gate unchanged (DOCUMENTS_EDIT). The API answers 404 for a notebook that is missing or isn't the caller's.
 */
export const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notebookId = '' } = useParams<{ notebookId: string }>();
  const { state: authState } = useAuthContext();
  const isMobile = useIsMobile();
  const { notebook, isLoading, isNotFound, refetch } = Notebooks.Hooks.useSingle({ id: notebookId });

  // Three side-by-side panels need the width, so the nav collapses to its icon
  // rail on entry and is restored on leave. Not full focus mode: this page has
  // no header of its own and would lose the shell header + mobile dock.
  useCollapseSidebarOnMount({ focusMode: false });

  useEffect(() => {
    if (authState.isFetched && !authState.permissions.includes(PERMISSIONS.DOCUMENTS_EDIT)) navigate('/app');
  }, [authState.isFetched, authState.permissions, navigate]);

  // `key` gives each notebook a fresh workspace: source picks, the active source
  // and an open review belong to one notebook and must never carry over to another.
  // A 404 outranks a cached copy: a notebook deleted (or no longer yours) since it was last
  // seen must not keep opening from cache.
  const content =
    notebook && !isNotFound ? (
      <NotebookWorkspace
        key={notebook._id}
        notebookId={notebook._id}
        title={Notebooks.getNotebookTitle(notebook, t('notebooks.default_title'))}
        phone={isMobile}
      />
    ) : (
      <NotebookStatus isLoading={isLoading} isNotFound={isNotFound} onRetry={() => refetch()} phone={isMobile} />
    );

  // Below `md` the workspace has to be pinned, not merely `h-full`. MobileShell
  // renders non-focus routes inside an auto-height padded wrapper, so a
  // percentage height there resolves to `auto`: the workspace collapsed to its
  // content, and the chat's inner flex chain (`flex-1` messages + fixed
  // composer) had no definite height to divide — leaving the composer past the
  // clipped bottom edge. MobileScreen pins to the shell's `relative` <main>
  // (`absolute inset-0`), which is the codebase's established way to get a true
  // viewport-height mobile screen regardless of how the flex chain resolves.
  // The tour target wrapper is itself a flex column so the workspace's header and
  // panel area still divide that height (a plain block wrapper would drop `flex-1`).
  // The phone workspace draws its own screen header (the notebook's name + panel
  // switcher) and clears the docked nav itself, since pinning bypasses the
  // wrapper's bottom padding.
  if (isMobile) {
    return (
      <MobileScreen>
        <div id="documents-page-container" data-tour="documents-page" className="flex h-full min-h-0 flex-col">
          {content}
        </div>
      </MobileScreen>
    );
  }

  return (
    <div id="documents-page-container" data-tour="documents-page" className="h-full">
      {content}
    </div>
  );
};

export default DocumentsPage;
